import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Trash2, Upload, Download, Pencil, X, Check, FlaskConical, ChevronLeft, ChevronRight, Factory, DollarSign, ArrowUpDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DEPARTMENTS = ['Biochemistry', 'Haematology', 'Microbiology', 'Serology', 'Immunology', 'Histopathology', 'Cytology', 'Radiology', 'General'];

export default function RateList() {
  const token = localStorage.getItem('vendor_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const fileRef = useRef(null);

  const [tests, setTests] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [departments, setDepartments] = useState([]);
  const [processingLabs, setProcessingLabs] = useState([]);
  const [selectedLab, setSelectedLab] = useState('');
  const [selected, setSelected] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editTest, setEditTest] = useState(null);
  const [form, setForm] = useState({ name: '', department: '', price: '', b2b_price: '', processing_lab: '', parameters: '', b2b_prices: [] });

  // Bulk B2B management state
  const [showBulkB2B, setShowBulkB2B] = useState(false);
  const [bulkLab, setBulkLab] = useState('');
  const [bulkPrice, setBulkPrice] = useState('');
  const [bulkAction, setBulkAction] = useState('set'); // set, delete, set_default
  const [compareLabs, setCompareLabs] = useState([]);

  const fetchTests = useCallback(async () => {
    const params = new URLSearchParams({ page, limit: 50 });
    if (search) params.append('search', search);
    if (deptFilter) params.append('department', deptFilter);
    const res = await fetch(`${API}/api/lis/tests?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    setTests(data.tests || []);
    setTotal(data.total || 0);
    setPages(data.pages || 0);
  }, [page, search, deptFilter, token]);

  const fetchMeta = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    const [dRes, lRes] = await Promise.all([
      fetch(`${API}/api/lis/tests/departments`, { headers: h }),
      fetch(`${API}/api/lis/tests/processing-labs`, { headers: h })
    ]);
    setDepartments(await dRes.json());
    setProcessingLabs(await lRes.json());
  }, [token]);

  useEffect(() => { fetchTests(); }, [fetchTests]);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  const openCreate = () => {
    setEditTest(null);
    setForm({ name: '', department: '', price: '', b2b_price: '', processing_lab: '', parameters: '', b2b_prices: [] });
    setShowDialog(true);
  };
  const openEdit = (t) => {
    setEditTest(t);
    setForm({ name: t.name, department: t.department, price: t.price, b2b_price: t.b2b_price, processing_lab: t.processing_lab || '', parameters: (t.parameters || []).join(', '), b2b_prices: t.b2b_prices || [] });
    setShowDialog(true);
  };

  const saveTest = async () => {
    const body = {
      ...form,
      price: parseFloat(form.price) || 0,
      b2b_price: parseFloat(form.b2b_price) || 0,
      parameters: form.parameters ? form.parameters.split(',').map(p => p.trim()).filter(Boolean) : []
    };
    const url = editTest ? `${API}/api/lis/tests/${editTest.id}` : `${API}/api/lis/tests`;
    const method = editTest ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
    if (res.ok) {
      toast.success(editTest ? 'Test updated' : 'Test created');
      setShowDialog(false);
      fetchTests();
      fetchMeta();
    } else {
      toast.error('Failed to save');
    }
  };

  const deleteTest = async (id) => {
    if (!window.confirm('Delete this test?')) return;
    await fetch(`${API}/api/lis/tests/${id}`, { method: 'DELETE', headers });
    toast.success('Deleted');
    fetchTests();
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} tests?`)) return;
    await fetch(`${API}/api/lis/tests/bulk-delete`, { method: 'POST', headers, body: JSON.stringify({ ids: selected }) });
    toast.success(`Deleted ${selected.length} tests`);
    setSelected([]);
    fetchTests();
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/api/lis/tests/bulk-upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    const data = await res.json();
    toast.success(`Uploaded ${data.created} tests`);
    if (data.errors?.length) toast.error(`${data.errors.length} errors`);
    fetchTests();
    fetchMeta();
    e.target.value = '';
  };

  const addB2BPrice = () => setForm(f => ({ ...f, b2b_prices: [...f.b2b_prices, { lab: '', price: '' }] }));
  const removeB2BPrice = (i) => setForm(f => ({ ...f, b2b_prices: f.b2b_prices.filter((_, j) => j !== i) }));
  const updateB2BPrice = (i, field, val) => setForm(f => {
    const arr = [...f.b2b_prices];
    arr[i] = { ...arr[i], [field]: field === 'price' ? parseFloat(val) || 0 : val };
    return { ...f, b2b_prices: arr };
  });

  // Bulk B2B price operations
  const executeBulkB2B = async () => {
    if (selected.length === 0) { toast.error('No tests selected'); return; }
    if (bulkAction !== 'delete' && !bulkPrice) { toast.error('Enter a price'); return; }
    if (bulkAction !== 'set_default' && !bulkLab) { toast.error('Select a processing lab'); return; }

    const body = {
      ids: selected,
      lab_name: bulkLab,
      action: bulkAction,
      price: parseFloat(bulkPrice) || 0,
    };
    const res = await fetch(`${API}/api/lis/tests/bulk-b2b-update`, { method: 'POST', headers, body: JSON.stringify(body) });
    if (res.ok) {
      const data = await res.json();
      const actionLabel = bulkAction === 'delete' ? 'Removed' : 'Updated';
      toast.success(`${actionLabel} B2B prices for ${data.updated} tests`);
      setShowBulkB2B(false);
      setBulkPrice('');
      setBulkLab('');
      fetchTests();
    } else {
      toast.error('Failed to update');
    }
  };

  const toggleAll = () => {
    if (selected.length === tests.length) setSelected([]);
    else setSelected(tests.map(t => t.id));
  };

  const getB2BForLab = (test, labName) => {
    const lab = labName || selectedLab;
    if (!lab) return test.b2b_price || 0;
    const match = (test.b2b_prices || []).find(p => p.lab === lab);
    return match ? match.price : test.b2b_price || 0;
  };

  const selectedTests = tests.filter(t => selected.includes(t.id));
  const totalMRP = selectedTests.reduce((s, t) => s + (t.price || 0), 0);
  const totalB2B = selectedTests.reduce((s, t) => s + getB2BForLab(t), 0);

  // Toggle compare lab columns
  const toggleCompareLab = (lab) => {
    setCompareLabs(prev => prev.includes(lab) ? prev.filter(l => l !== lab) : [...prev, lab]);
  };

  return (
    <div data-testid="lis-rate-list">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Rate List (Tests)</h1>
          <p className="text-sm text-slate-500">{total} tests</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/api/lis/business/templates/tests`, '_blank')} data-testid="download-template-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> Template
          </Button>
          <input type="file" ref={fileRef} accept=".csv" onChange={handleCSVUpload} className="hidden" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="csv-upload-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> CSV Upload
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="add-test-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Test
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4 border-slate-200">
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <Input placeholder="Search tests..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" data-testid="search-tests-input" />
            </div>
            <Select value={deptFilter} onValueChange={v => { setDeptFilter(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-[180px] h-9" data-testid="dept-filter"><SelectValue placeholder="All Departments" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={selectedLab} onValueChange={v => setSelectedLab(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[200px] h-9" data-testid="lab-b2b-filter"><SelectValue placeholder="B2B: Default Price" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Default B2B Price</SelectItem>
                {processingLabs.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Compare Labs Toggle */}
          {processingLabs.length > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-slate-500 flex items-center gap-1"><ArrowUpDown className="w-3 h-3" /> Compare:</span>
                {processingLabs.map(l => (
                  <button
                    key={l}
                    onClick={() => toggleCompareLab(l)}
                    data-testid={`compare-lab-${l}`}
                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${compareLabs.includes(l) ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selection Actions Bar */}
      {selected.length > 0 && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="selection-summary">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-blue-700 text-sm">{selected.length} tests selected</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs bg-white" onClick={() => setShowBulkB2B(true)} data-testid="bulk-b2b-btn">
                <Factory className="w-3 h-3 mr-1" /> Manage B2B Prices
              </Button>
              <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={bulkDelete} data-testid="bulk-delete-btn">
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
            </div>
          </div>
          <div className="flex gap-4 text-xs">
            <span className="text-slate-600">MRP Total: <strong>Rs.{totalMRP.toLocaleString()}</strong></span>
            <span className="text-slate-600">B2B{selectedLab ? ` (${selectedLab})` : ''}: <strong>Rs.{totalB2B.toLocaleString()}</strong></span>
            <span className="text-emerald-700">Margin: <strong>Rs.{(totalMRP - totalB2B).toLocaleString()}</strong></span>
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="p-3 text-left w-10"><input type="checkbox" checked={selected.length === tests.length && tests.length > 0} onChange={toggleAll} /></th>
                <th className="p-3 text-left font-medium text-slate-600">Test Name</th>
                <th className="p-3 text-left font-medium text-slate-600">Dept</th>
                <th className="p-3 text-left font-medium text-slate-600">Lab</th>
                <th className="p-3 text-center font-medium text-slate-600">Params</th>
                <th className="p-3 text-right font-medium text-slate-600">MRP</th>
                <th className="p-3 text-right font-medium text-slate-600">B2B{selectedLab ? ` (${selectedLab})` : ''}</th>
                {compareLabs.map(lab => (
                  <th key={lab} className="p-3 text-right font-medium text-indigo-600 text-xs whitespace-nowrap">{lab}</th>
                ))}
                <th className="p-3 text-right font-medium text-slate-600 w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tests.map(t => (
                <tr key={t.id} className={`border-b border-slate-100 hover:bg-slate-50 ${selected.includes(t.id) ? 'bg-blue-50/50' : ''}`}>
                  <td className="p-3"><input type="checkbox" checked={selected.includes(t.id)} onChange={() => setSelected(s => s.includes(t.id) ? s.filter(x => x !== t.id) : [...s, t.id])} /></td>
                  <td className="p-3 font-medium text-slate-800">{t.name}</td>
                  <td className="p-3 text-slate-500 text-xs">{t.department || '-'}</td>
                  <td className="p-3 text-slate-500 text-xs">{t.processing_lab || '-'}</td>
                  <td className="p-3 text-center text-slate-500 text-xs">{(t.parameters || []).length || '-'}</td>
                  <td className="p-3 text-right font-medium">Rs.{(t.price || 0).toLocaleString()}</td>
                  <td className="p-3 text-right font-medium text-blue-700">Rs.{getB2BForLab(t).toLocaleString()}</td>
                  {compareLabs.map(lab => {
                    const labPrice = getB2BForLab(t, lab);
                    const isCustom = (t.b2b_prices || []).some(p => p.lab === lab);
                    return (
                      <td key={lab} className={`p-3 text-right text-xs ${isCustom ? 'text-indigo-700 font-medium' : 'text-slate-400'}`}>
                        Rs.{labPrice.toLocaleString()}
                        {isCustom && <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-indigo-400"></span>}
                      </td>
                    );
                  })}
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => openEdit(t)} className="p-1 text-slate-400 hover:text-blue-600" data-testid={`edit-test-${t.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteTest(t.id)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`delete-test-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {tests.length === 0 && <tr><td colSpan={7 + compareLabs.length} className="p-8 text-center text-slate-400">No tests found. Add your first test or upload a CSV.</td></tr>}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-slate-200">
            <span className="text-xs text-slate-500">Page {page} of {pages}</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editTest ? 'Edit Test' : 'Add Test'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Test Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="test-name-input" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.department} onValueChange={v => setForm(f => ({ ...f, department: v }))}>
                <SelectTrigger data-testid="test-dept-select"><SelectValue placeholder="Department" /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.processing_lab} onValueChange={v => setForm(f => ({ ...f, processing_lab: v }))}>
                <SelectTrigger data-testid="test-lab-select"><SelectValue placeholder="Processing Lab" /></SelectTrigger>
                <SelectContent>{processingLabs.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder="MRP Price" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} data-testid="test-price-input" />
              <Input type="number" placeholder="Default B2B Price" value={form.b2b_price} onChange={e => setForm(f => ({ ...f, b2b_price: e.target.value }))} data-testid="test-b2b-input" />
            </div>
            <Input placeholder="Parameters (comma separated)" value={form.parameters} onChange={e => setForm(f => ({ ...f, parameters: e.target.value }))} data-testid="test-params-input" />

            {/* Multi-lab B2B Prices */}
            <div className="border border-slate-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Lab-wise B2B Prices</span>
                <Button variant="outline" size="sm" onClick={addB2BPrice} data-testid="add-b2b-price-btn"><Plus className="w-3 h-3 mr-1" /> Add Lab Price</Button>
              </div>
              {form.b2b_prices.map((bp, i) => (
                <div key={`el-${i}`} className="flex gap-2 mb-2 items-center">
                  <Select value={bp.lab} onValueChange={v => updateB2BPrice(i, 'lab', v)}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Select Lab" /></SelectTrigger>
                    <SelectContent>{processingLabs.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" placeholder="Price" value={bp.price} onChange={e => updateB2BPrice(i, 'price', e.target.value)} className="w-28" />
                  <button onClick={() => removeB2BPrice(i)} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                </div>
              ))}
              {form.b2b_prices.length === 0 && <p className="text-xs text-slate-400">No lab-specific prices. The default B2B price will be used.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={saveTest} data-testid="save-test-btn">{editTest ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk B2B Price Management Dialog */}
      <Dialog open={showBulkB2B} onOpenChange={setShowBulkB2B}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Factory className="w-5 h-5 text-blue-600" />
              Bulk B2B Price Management
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">{selected.length} tests selected</p>

            {/* Action selector */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: 'set', label: 'Set Lab Price', desc: 'Set B2B price for a processing lab' },
                { key: 'delete', label: 'Remove Lab Price', desc: 'Remove pricing for a lab' },
                { key: 'set_default', label: 'Set Default B2B', desc: 'Update the default B2B price' },
              ].map(a => (
                <button
                  key={a.key}
                  onClick={() => setBulkAction(a.key)}
                  data-testid={`bulk-action-${a.key}`}
                  className={`p-3 rounded-lg border text-left transition-colors ${bulkAction === a.key ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <p className="text-sm font-medium text-slate-800">{a.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{a.desc}</p>
                </button>
              ))}
            </div>

            {/* Lab selector (for set/delete) */}
            {bulkAction !== 'set_default' && (
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Processing Lab</label>
                <Select value={bulkLab} onValueChange={setBulkLab}>
                  <SelectTrigger data-testid="bulk-lab-select"><SelectValue placeholder="Select Processing Lab" /></SelectTrigger>
                  <SelectContent>{processingLabs.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            {/* Price input (for set/set_default) */}
            {bulkAction !== 'delete' && (
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">
                  {bulkAction === 'set_default' ? 'New Default B2B Price' : `B2B Price for ${bulkLab || '...'}`}
                </label>
                <Input
                  type="number"
                  placeholder="Enter price"
                  value={bulkPrice}
                  onChange={e => setBulkPrice(e.target.value)}
                  data-testid="bulk-price-input"
                />
              </div>
            )}

            {/* Preview: Show affected tests */}
            <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="border-b">
                    <th className="p-2 text-left text-slate-600">Test</th>
                    <th className="p-2 text-right text-slate-600">MRP</th>
                    <th className="p-2 text-right text-slate-600">Current B2B</th>
                    {bulkAction !== 'delete' && <th className="p-2 text-right text-blue-600">New B2B</th>}
                  </tr>
                </thead>
                <tbody>
                  {selectedTests.map(t => {
                    const currentB2B = bulkAction === 'set_default'
                      ? (t.b2b_price || 0)
                      : bulkLab
                        ? getB2BForLab(t, bulkLab)
                        : (t.b2b_price || 0);
                    return (
                      <tr key={t.id} className="border-b border-slate-100">
                        <td className="p-2 text-slate-700">{t.name}</td>
                        <td className="p-2 text-right">Rs.{(t.price || 0).toLocaleString()}</td>
                        <td className="p-2 text-right text-slate-500">Rs.{currentB2B.toLocaleString()}</td>
                        {bulkAction !== 'delete' && (
                          <td className="p-2 text-right text-blue-700 font-medium">
                            {bulkPrice ? `Rs.${parseFloat(bulkPrice).toLocaleString()}` : '-'}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkB2B(false)}>Cancel</Button>
            <Button
              onClick={executeBulkB2B}
              data-testid="bulk-b2b-execute-btn"
              variant={bulkAction === 'delete' ? 'destructive' : 'default'}
            >
              <Check className="w-3.5 h-3.5 mr-1" />
              {bulkAction === 'delete' ? `Remove from ${selected.length} tests` : `Apply to ${selected.length} tests`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
